import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import moment from 'moment'
import { nanoid } from 'nanoid'

import StyledDatePicker from './date-picker'
import Input from './input'
import { requestAnomalyGraphData, saveExplainSetup } from '../context/actions'
import { defaultAnomalyDateRangeLabel } from '../core/variables'
import { getAnomalyDateRange } from '../helpers'
import styles from '../styles/date-range-tabs.module.scss'

const yesterday = new Date()
yesterday.setDate(yesterday.getDate() - 1)

interface Props {
  selector: string
  onChange?: any
}

export default function DateRangeTabs({
  selector: anomalySelector,
  onChange,
}: Props): React.ReactElement {
  const dispatch = useDispatch()
  const [showDateSelector, setShowDateSelector] = useState(false)
  const [selectedValue, setSelectedValue] = useState('')
  const [startDate, setStartDate] = useState<Date | null>(yesterday)
  const [endDate, setEndDate] = useState<Date | null>(yesterday)
  const explainData = useSelector((s: any): any => s.explain)
  const metricsData = useSelector((s: any): any => s.metrics)

  useEffect((): void => {
    if (selectedValue === '') {
      setSelectedValue(anomalySelector)
    } else if (explainData.settings.dateRange !== selectedValue) {
      if (!onChange) {
        dispatch(
          saveExplainSetup({
            ...explainData,
            settings: { dateRange: selectedValue },
          }),
        )
      }
    }
    // eslint-disable-next-line
  }, [selectedValue, setSelectedValue, anomalySelector])

  const requestNewData = (data): void => {
    metricsData.currentKeyMetrics.forEach((item) => {
      const { metricID } = item
      const { to, from, selector } = data
      dispatch(requestAnomalyGraphData({ to, from, selector, metricID }))
    })
  }

  const handleButtonSelection = (dateRange: string): void => {
    const { from, to } = getAnomalyDateRange(dateRange)
    setSelectedValue(dateRange)

    if (dateRange !== selectedValue) {
      const rangeObject = {
        from,
        to,
        selector: dateRange,
      }
      if (onChange) {
        onChange(rangeObject)
      } else {
        requestNewData(rangeObject)
      }
    }
  }

  // TODO: add support for multiple instances by making name of the element per instance
  const name = 'picker'

  return (
    <div className={styles.wrapper}>
      <div className={styles.tabsWrapper}>
        <Input
          name={`select-date-${name}`}
          id={nanoid()}
          label="30d"
          type="radio"
          value="30"
          checked={selectedValue === 'Last 30days'}
          onClick={(event: React.MouseEvent): any => {
            handleButtonSelection('Last 30days')
            setShowDateSelector(false)
            event.preventDefault()
            event.stopPropagation()
          }}
        />
        <Input
          name={`select-date-${name}`}
          id={nanoid()}
          label="90d"
          type="radio"
          value="90"
          checked={selectedValue === 'Last 90days'}
          onClick={(event: React.MouseEvent): any => {
            handleButtonSelection('Last 90days')
            setShowDateSelector(false)
            event.preventDefault()
            event.stopPropagation()
          }}
        />
        <Input
          name={`select-date-${name}`}
          id={nanoid()}
          label="Year"
          type="radio"
          value="Year"
          checked={selectedValue === 'Year'}
          onClick={(event: React.MouseEvent): any => {
            handleButtonSelection('Year')
            setShowDateSelector(false)
            event.preventDefault()
            event.stopPropagation()
          }}
        />
        <Input
          name={`select-date-${name}`}
          id={nanoid()}
          label="Custom"
          type="radio"
          value="Custom"
          checked={
            selectedValue === 'Custom' || selectedValue.indexOf('-') !== -1
          }
          onClick={(event: React.MouseEvent): any => {
            setSelectedValue('Custom')
            setShowDateSelector(!showDateSelector)
            event.preventDefault()
            event.stopPropagation()
          }}
        />
      </div>
      {showDateSelector ? (
        <div className={styles.dateWrapper}>
          <div
            className={styles.datePicker}
            onClick={(e: React.SyntheticEvent): void => e.stopPropagation()}
            onKeyDown={(e: React.SyntheticEvent): void => e.stopPropagation()}
            role="button"
            tabIndex={-1}
          >
            <StyledDatePicker
              selectsRange
              isClearable={false}
              wrapperClassName={styles.calendar}
              startDate={startDate}
              endDate={endDate}
              onChange={(dates) => {
                const [start, end] = dates

                setStartDate(start)
                setEndDate(end)

                if (start && end) {
                  const from = moment(start).format('YYYYMMDD')
                  const to = moment(end).format('YYYYMMDD')

                  if (to !== from) {
                    const newSelector = `${from} - ${to}`
                    const rangeObject = {
                      from,
                      to,
                      selector: newSelector,
                    }
                    if (onChange) {
                      onChange(rangeObject)
                    } else {
                      requestNewData(rangeObject)
                    }
                    setSelectedValue(newSelector)

                    setShowDateSelector(false)
                  }
                }
              }}
            />
          </div>
        </div>
      ) : null}
    </div>
  )
}

DateRangeTabs.defaultProps = {
  selector: defaultAnomalyDateRangeLabel,
}
